import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Quote, TrendingUp, AlertTriangle } from 'lucide-react';
import { getDebateDetail } from '../../services/api';
import './DebatePage.css';

const POSITION_TONE = {
  매수: { bg: '#ecfdf5', fg: '#0f766e', border: 'rgba(15,118,110,0.25)' },
  숏: { bg: '#fef2f2', fg: '#b42318', border: 'rgba(180,35,24,0.25)' },
  패스: { bg: '#fef2f2', fg: '#b42318', border: 'rgba(180,35,24,0.25)' },
  조건부: { bg: '#f3effc', fg: '#5b4ab5', border: 'rgba(91,74,181,0.25)' },
  관망: { bg: '#fff7ed', fg: '#b45309', border: 'rgba(180,83,9,0.25)' },
};

const SHIFT_LABEL = {
  강화: { text: '강화', tone: '#0f766e', arrow: '↑' },
  보수화: { text: '보수화', tone: '#b45309', arrow: '↓' },
  유지: { text: '유지', tone: '#64748b', arrow: '·' },
};

const KIND_LABEL = {
  opening: '오프닝',
  rebut: '반박',
  accept: '수용',
};

// 아바타/칩에 표시할 짧은 별명. nickname 풀버전은 닉라인에 유지
const SHORT_NICK = {
  wb: '현인',
  cw: '여왕',
  jc: '사냥꾼',
  rd: '원칙',
  tl: '퍼마불',
  pt: '사도',
  pl: '항해사',
  hm: '시인',
};

const shortNick = (speaker) =>
  SHORT_NICK[speaker?.id] || speaker?.initial || '?';

const positionStyle = (position) => POSITION_TONE[position] || POSITION_TONE.조건부;

const DebatePage = () => {
  const { aiDebateNo } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSpeaker, setActiveSpeaker] = useState(null);
  const messageRefs = useRef({});

  useEffect(() => {
    if (!aiDebateNo) return;
    let aborted = false;

    (async () => {
      try {
        setLoading(true);
        const json = await getDebateDetail(aiDebateNo);
        if (aborted) return;
        if (!json) {
          setError('토론 데이터를 찾을 수 없습니다.');
          return;
        }
        setData(json);
      } catch (e) {
        if (!aborted) setError(e.message);
      } finally {
        if (!aborted) setLoading(false);
      }
    })();

    return () => { aborted = true; };
  }, [aiDebateNo]);

  const speakerMap = useMemo(() => {
    if (!data) return {};
    return Object.fromEntries(data.speakers.map((s) => [s.id, s]));
  }, [data]);

  const messagesByRound = useMemo(() => {
    if (!data) return {};
    return data.messages.reduce((acc, m) => {
      (acc[m.round] = acc[m.round] || []).push(m);
      return acc;
    }, {});
  }, [data]);

  const scrollToMessage = (mid) => {
    const el = messageRefs.current[mid];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('debate-msg--flash');
    setTimeout(() => el.classList.remove('debate-msg--flash'), 1400);
  };

  if (loading) {
    return (
      <div className="debate-page debate-page--state">
        <div className="debate-state-spinner" />
        <p className="debate-state-text">거장들을 소집하는 중…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="debate-page debate-page--state">
        <button className="debate-back-btn" onClick={() => navigate(-1)} aria-label="뒤로">
          <ArrowLeft size={20} />
        </button>
        <AlertTriangle size={28} color="#b42318" />
        <p className="debate-state-text">토론 데이터를 불러오지 못했습니다.</p>
        <p className="debate-state-sub">{error}</p>
      </div>
    );
  }

  const { topic, asOf, context, speakers, rounds, summary } = data;
  const asOfLabel = asOf ? new Date(asOf).toISOString().slice(0, 10).replace(/-/g, '.') : '';
  const change = context?.change1d || '';
  const changePositive = change.startsWith('+');

  return (
    <div className="debate-page">
      {/* ── Header ─────────────────────────────────── */}
      <header className="debate-header">
        <div className="debate-header-top">
          <button className="debate-back-btn" onClick={() => navigate(-1)} aria-label="뒤로">
            <ArrowLeft size={20} />
          </button>
          <div className="debate-header-eyebrow">INVESTMENT DEBATE · AI 거장 토론</div>
          <div className="debate-header-spacer" />
        </div>

        <div className="debate-ticker-row">
          <div className="debate-ticker">
            <span className="debate-ticker-dollar">$</span>
            <span className="debate-ticker-symbol">{data.ticker}</span>
          </div>
          {context?.price && (
            <div className="debate-price-block">
              <span className="debate-price">{context.price}</span>
              {change && (
                <span className={`debate-change ${changePositive ? 'is-up' : 'is-down'}`}>
                  {changePositive ? '▲' : '▼'} {change.replace(/^[+-]/, '')}
                </span>
              )}
            </div>
          )}
          {asOfLabel && <div className="debate-date">{asOfLabel}</div>}
        </div>

        <h1 className="debate-topic">{topic}</h1>

        {context?.catalyst && (
          <div className="debate-catalyst">
            <span className="debate-catalyst-tag">Catalyst</span>
            <span className="debate-catalyst-text">{context.catalyst}</span>
          </div>
        )}

        {context?.keyMetrics?.length > 0 && (
          <div className="debate-metrics">
            {context.keyMetrics.map((m, i) => (
              <div key={i} className="debate-metric">
                <div className="debate-metric-label">{m.label}</div>
                <div className="debate-metric-value-row">
                  <span className="debate-metric-value">{m.value}</span>
                  {m.delta && (
                    <span className={`debate-metric-delta ${m.delta.startsWith('+') ? 'is-up' : m.delta.startsWith('↓') ? 'is-down' : ''}`}>
                      {m.delta}
                    </span>
                  )}
                </div>
                {m.note && <div className="debate-metric-note">{m.note}</div>}
              </div>
            ))}
          </div>
        )}
      </header>

      {/* ── Roster ─────────────────────────────────── */}
      <section className="debate-roster">
        <div className="debate-roster-title">
          <MessageSquare size={14} />
          <span>참여 거장 {speakers.length}명</span>
        </div>
        <div className="debate-roster-grid">
          {speakers.map((s) => {
            const tone = positionStyle(s.finalPosition);
            const active = activeSpeaker === s.id;
            return (
              <button
                key={s.id}
                className={`debate-roster-card ${active ? 'is-active' : ''}`}
                onClick={() => setActiveSpeaker(active ? null : s.id)}
                style={{
                  '--card-accent': s.positionColor,
                  '--pos-bg': tone.bg,
                  '--pos-fg': tone.fg,
                  '--pos-border': tone.border,
                }}
              >
                <div className="debate-avatar" style={{ background: s.positionColor }}>
                  {shortNick(s)}
                </div>
                <div className="debate-roster-info">
                  <div className="debate-roster-nick">{s.nickname}</div>
                  <div className="debate-roster-phil">{s.philosophy}</div>
                </div>
                <span className="debate-position-pill">{s.finalPosition}</span>
              </button>
            );
          })}
        </div>
        {activeSpeaker && (
          <div className="debate-roster-stance">
            <Quote size={14} />
            <span>{speakerMap[activeSpeaker]?.stance}</span>
          </div>
        )}
      </section>

      {/* ── Chat thread (KakaoTalk style) ───────────── */}
      <main className="debate-thread">
        {rounds.map((r, rIdx) => {
          const msgs = messagesByRound[r.round] || [];
          return (
            <section key={r.round} className="debate-round">
              <div className="debate-round-divider">
                <span className="debate-round-chip">Round {r.round} · {r.label}</span>
              </div>

              <ul className="debate-msg-list">
                {msgs.map((m, i) => {
                  const speaker = speakerMap[m.speakerId];
                  if (!speaker) return null;
                  const tone = positionStyle(m.position);
                  const prev = i > 0 ? msgs[i - 1] : null;
                  const sameAsPrev = prev && prev.speakerId === m.speakerId;
                  const replies = (m.replyTo || []).map((id) => {
                    const target = data.messages.find((x) => x.id === id);
                    if (!target) return null;
                    return { id, msg: target, speaker: speakerMap[target.speakerId] };
                  }).filter(Boolean);
                  const shift = m.shift ? SHIFT_LABEL[m.shift] : null;
                  const dimmed = activeSpeaker && activeSpeaker !== m.speakerId;
                  // 시간 시뮬레이션: 라운드 시작 시각 + 메시지 인덱스 분
                  const baseHour = rIdx === 0 ? 9 : 10;
                  const mm = String(i * 3 + 5).padStart(2, '0');
                  const timeLabel = `${baseHour}:${mm.slice(-2)}`;

                  return (
                    <li
                      key={m.id}
                      ref={(el) => { messageRefs.current[m.id] = el; }}
                      className={`debate-msg ${dimmed ? 'is-dimmed' : ''} ${sameAsPrev ? 'is-grouped' : ''}`}
                      style={{
                        '--accent': speaker.positionColor,
                        '--pos-bg': tone.bg,
                        '--pos-fg': tone.fg,
                        '--pos-border': tone.border,
                      }}
                    >
                      <div className="debate-msg-avatar-col">
                        {!sameAsPrev && (
                          <button
                            className="debate-msg-avatar"
                            onClick={() => setActiveSpeaker(activeSpeaker === speaker.id ? null : speaker.id)}
                            style={{ background: speaker.positionColor }}
                            aria-label={speaker.nickname}
                          >
                            {shortNick(speaker)}
                          </button>
                        )}
                      </div>

                      <div className="debate-msg-col">
                        {!sameAsPrev && (
                          <div className="debate-msg-nickline">
                            <span className="debate-msg-nick">{speaker.nickname}</span>
                            <span className="debate-msg-phil">{speaker.philosophy}</span>
                          </div>
                        )}

                        <div className="debate-msg-bubble-row">
                          <div className="debate-msg-bubble">
                            {replies.length > 0 && (
                              <div className="debate-reply-preview-list">
                                {replies.map((rep) => (
                                  <button
                                    key={rep.id}
                                    className="debate-reply-preview"
                                    onClick={() => scrollToMessage(rep.id)}
                                    style={{ '--reply-accent': rep.speaker?.positionColor || '#94a3b8' }}
                                  >
                                    <span className="debate-reply-preview-nick">
                                      ↳ @{rep.speaker?.nickname || '?'}
                                    </span>
                                    <span className="debate-reply-preview-text">
                                      {rep.msg.text}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}

                            <div className="debate-bubble-meta">
                              <span className="debate-position-pill">{m.position}</span>
                              {shift && (
                                <span className="debate-shift" style={{ color: shift.tone }}>
                                  {shift.arrow} {shift.text}
                                </span>
                              )}
                              <span className="debate-msg-kind">{KIND_LABEL[m.kind] || m.kind}</span>
                            </div>

                            <p className="debate-msg-text">{m.text}</p>
                          </div>

                          <span className="debate-msg-time">{timeLabel}</span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </main>

      {/* ── Summary ─────────────────────────────────── */}
      {summary && (
        <section className="debate-summary">
          <div className="debate-summary-title">
            <TrendingUp size={14} />
            <span>토론 결론</span>
          </div>

          {summary.polarization && (
            <p className="debate-polarization">{summary.polarization}</p>
          )}

          {summary.finalPositions && (
            <div className="debate-final-grid">
              {Object.entries(summary.finalPositions).map(([pos, ids]) => {
                if (!ids?.length) return null;
                const tone = positionStyle(pos);
                return (
                  <div
                    key={pos}
                    className="debate-final-row"
                    style={{ '--pos-bg': tone.bg, '--pos-fg': tone.fg, '--pos-border': tone.border }}
                  >
                    <span className="debate-final-label">{pos}</span>
                    <span className="debate-final-count">{ids.length}</span>
                    <div className="debate-final-people">
                      {ids.map((id) => {
                        const s = speakerMap[id];
                        if (!s) return null;
                        return (
                          <button
                            key={id}
                            className="debate-final-chip"
                            onClick={() => setActiveSpeaker(activeSpeaker === id ? null : id)}
                            style={{ background: s.positionColor }}
                            title={s.nickname}
                          >
                            {shortNick(s)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {summary.shifts && (
            <div className="debate-shift-board">
              <div className="debate-shift-board-title">포지션 변화</div>
              <div className="debate-shift-board-grid">
                {Object.entries(summary.shifts).map(([id, shift]) => {
                  const s = speakerMap[id];
                  if (!s) return null;
                  const lab = SHIFT_LABEL[shift] || SHIFT_LABEL.유지;
                  return (
                    <div key={id} className="debate-shift-item">
                      <span className="debate-shift-avatar" style={{ background: s.positionColor }}>
                        {shortNick(s)}
                      </span>
                      <span className="debate-shift-text" style={{ color: lab.tone }}>
                        {lab.arrow} {lab.text}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {summary.convergence?.length > 0 && (
            <div className="debate-block">
              <div className="debate-block-title">합의 지점</div>
              <ul className="debate-bullet-list">
                {summary.convergence.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          )}

          {summary.watchItems?.length > 0 && (
            <div className="debate-block">
              <div className="debate-block-title">지켜볼 항목</div>
              <ul className="debate-bullet-list debate-bullet-list--watch">
                {summary.watchItems.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* ── Sources ─────────────────────────────────── */}
      {context?.sources?.length > 0 && (
        <footer className="debate-sources">
          <div className="debate-sources-title">자료</div>
          <ul>
            {context.sources.map((s, i) => (<li key={i}>· {s}</li>))}
          </ul>
        </footer>
      )}

      <div className="debate-bottom-pad" />
    </div>
  );
};

export default DebatePage;
